import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Upload, Trash2, Image, Tag, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminImagesTab() {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("");
  const [toast, setToast] = useState(null);

  const show = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/images`, { withCredentials: true });
      setImages(res.data.images || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("location", "");
      fd.append("alt_text", file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "));
      try {
        await axios.post(`${API}/admin/images`, fd, { withCredentials: true });
        uploaded++;
      } catch (err) {
        show(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`, "error");
      }
    }
    if (uploaded > 0) show(`${uploaded} image(s) uploaded`);
    setUploading(false);
    e.target.value = "";
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image permanently?")) return;
    try {
      await axios.delete(`${API}/admin/images/${id}`, { withCredentials: true });
      show("Image deleted");
      load();
    } catch (e) {
      show("Failed to delete", "error");
    }
  };

  const handleUpdateLocation = async (id, location) => {
    try {
      await axios.put(`${API}/admin/images/${id}`, { location }, { withCredentials: true });
      setImages(prev => prev.map(img => img.id === id ? { ...img, location } : img));
    } catch (e) {
      show("Failed to update", "error");
    }
  };

  const handleUpdateAlt = async (id, alt_text) => {
    try {
      await axios.put(`${API}/admin/images/${id}`, { alt_text }, { withCredentials: true });
      setImages(prev => prev.map(img => img.id === id ? { ...img, alt_text } : img));
    } catch (e) {
      show("Failed to update", "error");
    }
  };

  const filtered = filter
    ? images.filter(img =>
        img.original_name?.toLowerCase().includes(filter.toLowerCase()) ||
        img.location?.toLowerCase().includes(filter.toLowerCase()) ||
        img.alt_text?.toLowerCase().includes(filter.toLowerCase())
      )
    : images;

  const locations = [...new Set(images.map(i => i.location).filter(Boolean))];

  return (
    <div className="space-y-5" data-testid="admin-images-tab">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === "error" ? "bg-red-600 text-white" : "bg-emerald-600 text-white"
        }`}>{toast.msg}</div>
      )}

      {/* Upload Area */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4 text-slate-400" /> Upload Images
          </CardTitle>
        </CardHeader>
        <CardContent>
          <label
            data-testid="image-upload-area"
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 cursor-pointer transition-colors ${
              uploading ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:border-[#002147] hover:bg-slate-50"
            }`}
          >
            <Image className="h-8 w-8 text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-600">
              {uploading ? "Uploading..." : "Click to upload or drag images here"}
            </p>
            <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP, GIF, SVG — Max 10MB each</p>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </CardContent>
      </Card>

      {/* Gallery */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base">Image Library ({images.length})</CardTitle>
            <div className="flex items-center gap-2">
              {locations.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {locations.map(loc => (
                    <Badge
                      key={loc}
                      variant="outline"
                      className={`text-[10px] cursor-pointer ${filter === loc ? "bg-[#002147] text-white border-[#002147]" : ""}`}
                      onClick={() => setFilter(filter === loc ? "" : loc)}
                    >
                      {loc}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="image-search"
                  placeholder="Search..."
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  className="pl-8 h-8 w-40 text-xs"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Image className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{images.length === 0 ? "No images uploaded yet" : "No images match your search"}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map(img => (
                <div key={img.id} data-testid={`image-card-${img.id}`} className="group border rounded-xl overflow-hidden bg-white">
                  <div className="aspect-square relative bg-slate-100">
                    <img
                      src={`${API}/uploads/${img.filename}`}
                      alt={img.alt_text || img.original_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <button
                      onClick={() => handleDelete(img.id)}
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {img.location && (
                      <div className="absolute bottom-2 left-2">
                        <Badge className="text-[9px] bg-[#002147] text-white border-0">{img.location}</Badge>
                      </div>
                    )}
                  </div>
                  <div className="p-2.5 space-y-1.5">
                    <p className="text-[10px] text-slate-400 truncate">{img.original_name}</p>
                    <div className="flex items-center gap-1">
                      <Tag className="h-3 w-3 text-slate-300 shrink-0" />
                      <input
                        placeholder="Location tag (e.g. hero)"
                        value={img.location || ""}
                        onChange={e => setImages(prev => prev.map(i => i.id === img.id ? { ...i, location: e.target.value } : i))}
                        onBlur={e => handleUpdateLocation(img.id, e.target.value)}
                        className="text-[10px] border-0 border-b border-transparent focus:border-slate-200 outline-none w-full py-0.5 bg-transparent"
                      />
                    </div>
                    <input
                      placeholder="Alt text"
                      value={img.alt_text || ""}
                      onChange={e => setImages(prev => prev.map(i => i.id === img.id ? { ...i, alt_text: e.target.value } : i))}
                      onBlur={e => handleUpdateAlt(img.id, e.target.value)}
                      className="text-[10px] text-slate-500 border-0 border-b border-transparent focus:border-slate-200 outline-none w-full py-0.5 bg-transparent"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
